import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/SearchableSelect';
import { toast } from 'sonner';
import { groupByMonth } from '@/utils/groupByMonth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STAGES = ['Cutting', 'Forging', 'Machining', 'HT', 'FG'];

export default function PartConversion() {
  const [conversions, setConversions] = useState([]);
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const canModify = (entry) => user.role === 'admin' || entry.created_by === user.username;
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    from_part: '',
    to_part: '',
    stage_from: '',
    stage_to: 'FG',
    qty: ''
  });

  const fetchConversions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/transactions/conversion`, {
        
      });
      setConversions(response.data);
    } catch (err) { console.error(err);
      toast.error('Failed to load conversions');
    }
  }, []);

  const fetchItems = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/masters/items`, {
        
      });
      setItems(response.data);
    } catch (err) { console.error("Failed to load dropdown data:", err); }
  }, []);

  useEffect(() => {
    fetchConversions();
    fetchItems();
  }, [fetchConversions, fetchItems]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.stage_from) {
      toast.error('Please select the source stage (From Stage)');
      return;
    }
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const data = {
        date: new Date(formData.date).toISOString(),
        from_part: formData.from_part,
        to_part: formData.to_part,
        stage_from: formData.stage_from,
        stage_to: formData.stage_to,
        qty: parseInt(formData.qty),
        created_by: user.username
      };

      await axios.post(`${API}/transactions/conversion`, data, {
        
      });
      toast.success('Part conversion created successfully');
      setShowForm(false);
      setFormData({
        date: new Date().toISOString().split('T')[0],
        from_part: '',
        to_part: '',
        stage_from: '',
        stage_to: 'FG',
        qty: ''
      });
      fetchConversions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create conversion');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this conversion?')) return;
    try {
      await axios.delete(`${API}/transactions/conversion/${id}`, {
        
      });
      toast.success('Conversion deleted successfully');
      fetchConversions();
    } catch (err) { console.error(err);
      toast.error('Failed to delete conversion');
    }
  };

  const toggleMonth = (key) => setCollapsedMonths(prev => ({...prev, [key]: !prev[key]}));
  const [collapsedMonths, setCollapsedMonths] = useState({});
  const monthGroups = groupByMonth(conversions);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}} data-testid="part-conversion-title">PART CONVERSION</h1>
          <p className="text-sm text-gray-700 mt-2">Convert parts from one type to another at a specific stage</p>
        </div>
        <Button
          data-testid="toggle-conversion-form-button"
          onClick={() => setShowForm(!showForm)}
          className="bg-accent text-accent-foreground hover:bg-accent-hover rounded-sm font-medium"
        >
          <Plus className="w-4 h-4 mr-2" />
          {showForm ? 'CANCEL' : 'NEW CONVERSION'}
        </Button>
      </div>

      {showForm && (
        <div className="bg-white border border-border shadow-sm p-6">
          <h2 className="text-2xl font-bold uppercase mb-6" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>NEW PART CONVERSION</h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Date</Label>
              <Input
                data-testid="conversion-date-input"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                required
                className="mt-1.5 rounded-none"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">From Part</Label>
              <SearchableSelect
                testId="conversion-from-part-select"
                className="mt-1.5"
                options={items.map(item => ({ value: item.part_id, label: `${item.part_id} - ${item.part_name}`, searchStr: `${item.part_id} ${item.part_name}` }))}
                value={formData.from_part}
                onValueChange={(value) => setFormData({...formData, from_part: value})}
                placeholder="Search part..."
                searchPlaceholder="Type to search parts..."
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">From Stage</Label>
              <Select value={formData.stage_from} onValueChange={(v) => setFormData({...formData, stage_from: v})}>
                <SelectTrigger data-testid="conversion-stage-from-select" className="mt-1.5 rounded-none">
                  <SelectValue placeholder="Select stage..." />
                </SelectTrigger>
                <SelectContent>
                  {STAGES.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">To Part</Label>
              <SearchableSelect
                testId="conversion-to-part-select"
                className="mt-1.5"
                options={items.map(item => ({ value: item.part_id, label: `${item.part_id} - ${item.part_name}`, searchStr: `${item.part_id} ${item.part_name}` }))}
                value={formData.to_part}
                onValueChange={(value) => setFormData({...formData, to_part: value})}
                placeholder="Search part..."
                searchPlaceholder="Type to search parts..."
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">To Stage</Label>
              <Select value={formData.stage_to} onValueChange={(v) => setFormData({...formData, stage_to: v})}>
                <SelectTrigger data-testid="conversion-stage-to-select" className="mt-1.5 rounded-none">
                  <SelectValue placeholder="Select stage..." />
                </SelectTrigger>
                <SelectContent>
                  {STAGES.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Quantity</Label>
              <Input
                data-testid="conversion-qty-input"
                type="number"
                value={formData.qty}
                onChange={(e) => setFormData({...formData, qty: e.target.value})}
                required
                className="mt-1.5 rounded-none"
              />
            </div>
            <div className="col-span-3 flex justify-end pt-4">
              <Button
                data-testid="submit-conversion-button"
                type="submit"
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm"
              >
                CREATE CONVERSION
              </Button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white border border-border shadow-sm">
        <div className="border-b border-border p-4 bg-gray-50/50">
          <h2 className="text-xl font-semibold tracking-tight uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>RECENT CONVERSIONS</h2>
        </div>
        {conversions.length === 0 ? (
          <div className="p-12 text-center text-gray-700">No conversions found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full caption-bottom text-sm">
              <thead>
                <tr className="border-b">
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Date</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">From Part</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">From Stage</th>
                  <th className="h-10 px-4 text-center align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">-&gt;</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">To Part</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">To Stage</th>
                  <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Quantity</th>
                  <th className="h-10 px-4 text-center align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Actions</th>
                </tr>
              </thead>
              {monthGroups.map(group => (
                <tbody key={`month-group-${group.key}`}>
                  <tr className="bg-gray-100 cursor-pointer" onClick={() => toggleMonth(group.key)}>
                    <td colSpan={8} className="p-3">
                      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-text-muted">
                        {collapsedMonths[group.key] ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        {group.label} ({group.entries.length} entries)
                      </div>
                    </td>
                  </tr>
                  {!collapsedMonths[group.key] && group.entries.map((conversion, idx) => (
                    <tr key={conversion.id || idx} className="border-b transition-colors hover:bg-muted/50" data-testid={`conversion-row-${idx}`}>
                      <td className="p-4 align-middle font-mono text-xs">{new Date(conversion.date).toLocaleDateString()}</td>
                      <td className="p-4 align-middle font-mono text-xs">{conversion.from_part}</td>
                      <td className="p-4 align-middle text-xs">
                        <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium bg-amber-50 text-amber-700 border-amber-200">
                          {conversion.stage_from || '—'}
                        </span>
                      </td>
                      <td className="p-4 align-middle text-center text-accent font-bold">-&gt;</td>
                      <td className="p-4 align-middle font-mono text-xs">{conversion.to_part}</td>
                      <td className="p-4 align-middle text-xs">
                        <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium bg-emerald-50 text-emerald-700 border-emerald-200">
                          {conversion.stage_to || 'FG'}
                        </span>
                      </td>
                      <td className="p-4 align-middle text-right font-mono text-xs">{conversion.qty}</td>
                      <td className="p-4 align-middle">
                        <div className="flex items-center justify-center">
                          {canModify(conversion) && (
                            <button
                              data-testid={`delete-conversion-${idx}`}
                              onClick={() => handleDelete(conversion.id)}
                              className="p-2 hover:bg-error/10 hover:text-error transition-all rounded-sm"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              ))}
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
